// Where the sun and moon are over Joutseno, and what the date and time are there.
// Pure numbers, no three.js, so it can be unit tested. The clock is the device's (UTC);
// the place is Joutseno, Lappeenranta, and its calendar is Finnish time (Europe/Helsinki).

/** Joutseno, Lappeenranta: latitude and longitude in degrees (east positive). */
export const JOUTSENO = { lat: 61.12, lon: 28.5, timeZone: 'Europe/Helsinki' } as const;

const RAD = Math.PI / 180;

/** A local date and time in Joutseno. `weekday` is 0 = Sunday … 6 = Saturday; `month` is 1..12. */
export interface LocalTime { year: number; month: number; day: number; hour: number; minute: number; weekday: number }

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
let localFormat: Intl.DateTimeFormat | null = null;

/** The date and time in Joutseno (Finnish time, with summer time) at an instant. */
export function joutsenoTime(ms: number): LocalTime {
  localFormat ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: JOUTSENO.timeZone, year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', weekday: 'short', hourCycle: 'h23',
  });
  const parts: Record<string, string> = {};
  for (const p of localFormat.formatToParts(new Date(ms))) parts[p.type] = p.value;
  return {
    year: Number(parts.year), month: Number(parts.month), day: Number(parts.day),
    hour: Number(parts.hour) % 24, minute: Number(parts.minute), weekday: WEEKDAYS.indexOf(parts.weekday ?? ''),
  };
}

/** The instant (ms) of a Joutseno local date and time. */
export function joutsenoInstant(year: number, month: number, day: number, hour = 12, minute = 0): number {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let ms = wall - 2 * 3600_000;
  // Finnish time is UTC+2 or UTC+3; two corrections settle it, including near the changeovers.
  for (let i = 0; i < 2; i++) {
    const t = joutsenoTime(ms);
    ms += wall - Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute);
  }
  return ms;
}

/**
 * Parses `?time=`: a Joutseno local time `YYYY-MM-DD` (noon) or `YYYY-MM-DDTHH:MM`, or any
 * time with an explicit offset or `Z`. Returns the instant in ms, or null if it is not one.
 */
export function parseJoutsenoTime(text: string | null): number | null {
  if (!text) return null;
  const local = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/.exec(text.trim());
  if (local) {
    const [y, mo, d, h, mi] = local.slice(1).map((v) => (v === undefined ? NaN : Number(v)));
    if (mo! < 1 || mo! > 12 || d! < 1 || d! > 31) return null;
    const hour = Number.isNaN(h!) ? 12 : h!, minute = Number.isNaN(mi!) ? 0 : mi!;
    if (hour > 23 || minute > 59) return null;
    return joutsenoInstant(y!, mo!, d!, hour, minute);
  }
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/.test(text.trim())) return null;
  const ms = Date.parse(text.trim());
  return Number.isFinite(ms) ? ms : null;
}

/** A body's place in the sky: elevation above the horizon and compass azimuth (0 = north, 90 = east), degrees. */
export interface SkyPosition { elevation: number; azimuth: number }

function julianDay(ms: number): number { return ms / 86_400_000 + 2440587.5; }

/** The sun's position seen from a place (NOAA's solar position equations, with refraction). */
export function sunPosition(ms: number, lat: number = JOUTSENO.lat, lon: number = JOUTSENO.lon): SkyPosition {
  const T = (julianDay(ms) - 2451545) / 36525;
  const L0 = (((280.46646 + T * (36000.76983 + T * 0.0003032)) % 360) + 360) % 360;
  const M = 357.52911 + T * (35999.05029 - 0.0001537 * T);
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T);
  const C = Math.sin(M * RAD) * (1.914602 - T * (0.004817 + 0.000014 * T))
    + Math.sin(2 * M * RAD) * (0.019993 - 0.000101 * T) + Math.sin(3 * M * RAD) * 0.000289;
  const omega = 125.04 - 1934.136 * T;
  const lambda = L0 + C - 0.00569 - 0.00478 * Math.sin(omega * RAD);
  const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60;
  const eps = eps0 + 0.00256 * Math.cos(omega * RAD);
  const decl = Math.asin(Math.sin(eps * RAD) * Math.sin(lambda * RAD));
  const y = Math.tan((eps / 2) * RAD) ** 2;
  const eqTime = 4 / RAD * (y * Math.sin(2 * L0 * RAD) - 2 * e * Math.sin(M * RAD)
    + 4 * e * y * Math.sin(M * RAD) * Math.cos(2 * L0 * RAD)
    - 0.5 * y * y * Math.sin(4 * L0 * RAD) - 1.25 * e * e * Math.sin(2 * M * RAD));
  const utcMinutes = ((ms % 86_400_000) + 86_400_000) % 86_400_000 / 60_000;
  const solarMinutes = utcMinutes + eqTime + 4 * lon;
  const H = (solarMinutes / 4 - 180) * RAD;
  return horizontal(H, decl, lat * RAD, true);
}

/** The moon's position seen from a place (low-precision series, good to about a degree). */
export function moonPosition(ms: number, lat: number = JOUTSENO.lat, lon: number = JOUTSENO.lon): SkyPosition {
  const d = julianDay(ms) - 2451545;
  const L = (218.316 + 13.176396 * d) * RAD;
  const M = (134.963 + 13.064993 * d) * RAD;
  const F = (93.272 + 13.22935 * d) * RAD;
  const l = L + 6.289 * RAD * Math.sin(M);
  const b = 5.128 * RAD * Math.sin(F);
  const e = 23.4397 * RAD;
  const ra = Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l));
  const dec = Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l));
  const sidereal = (280.16 + 360.9856235 * d + lon) * RAD;
  return horizontal(sidereal - ra, dec, lat * RAD, false);
}

function horizontal(H: number, dec: number, phi: number, refract: boolean): SkyPosition {
  let el = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H)) / RAD;
  const az = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi)) / RAD + 180;
  if (refract && el > -1) el += 1.02 / Math.tan((el + 10.3 / (el + 5.11)) * RAD) / 60;
  return { elevation: el, azimuth: ((az % 360) + 360) % 360 };
}

/** Compass bearing of the world's +x axis (start to finish): the course runs east to west, the lake (+z) lies north. */
export const COURSE_BEARING = 270;

/** World direction (normalised x, y, z) towards a body at a compass azimuth and elevation, in degrees. */
export function skyDirection(elevation: number, azimuth: number): [number, number, number] {
  // Seen from above, the world turns from +x to +z clockwise, as the compass does.
  const a = (azimuth - COURSE_BEARING) * RAD, el = elevation * RAD;
  const c = Math.cos(el);
  return [c * Math.cos(a), Math.sin(el), c * Math.sin(a)];
}
